import React, { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Settings as SettingsIcon } from 'lucide-react';
import { AISettingsPanel } from '@/routes/organization/AISettings';
import { ThemeSettingsPanel } from '@/routes/organization/ThemeSettings';
import { TranslationsPanel } from '@/routes/organization/TranslationOverrides';
import { RolesAndPermissionsPanel } from '@/routes/organization/RolesAndPermissions';
import { PlatformSharingPanel } from './panels/PlatformSharingPanel';
import { MembersAndInvitesPanel } from './panels/MembersAndInvitesPanel';
// AC-ALX-6: KnowledgeRAGPanel removed — Embedding + Backfill now live in AI & LLM tab
import { t } from '@/lib/i18n';
// NAV-1: Constitution moved to Structure Settings (governance ≠ system config)

interface TabDef {
  value: string;
  label: string;
  /** Tab is visible if the user has ANY of these permissions. Null = always visible. */
  anyPermissions: string[] | null;
  render: () => React.ReactNode;
}

/**
 * System Settings — organization-scoped settings surfaced inside the ROROS
 * product sidebar. All tabs write to the active org; none of them touch
 * platform-wide state.
 *
 * Each tab is individually gated on its relevant permission so that users
 * with limited access (e.g. only MANAGE_ROLES) still see the tabs they can
 * use. Tabs the user can't access are hidden entirely rather than shown
 * as disabled — this matches the StructureSettings pattern and keeps
 * Radix Tabs trigger/content pairing stable.
 */
export function SystemSettingsPage() {
  const { hasAnyPermission } = usePermissions();

  const allTabs: TabDef[] = [
    {
      value: 'ai',
      label: 'AI & LLM',
      anyPermissions: [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS],
      render: () => <AISettingsPanel />,
    },
    {
      value: 'design',
      label: 'Design System',
      anyPermissions: [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS],
      render: () => <ThemeSettingsPanel />,
    },
    {
      value: 'sharing',
      label: 'Platform Sharing',
      anyPermissions: [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS],
      render: () => <PlatformSharingPanel />,
    },
    {
      // The standalone Language tab was merged into Translations on
      // 2026-04-15 — TranslationsPanel renders LanguagePanel internally.
      // Tab value stays 'translations' for stability.
      value: 'translations',
      label: 'Language & Translation',
      anyPermissions: [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS],
      render: () => <TranslationsPanel />,
    },
    {
      value: 'roles',
      label: 'Roles & Permissions',
      anyPermissions: [PERMISSIONS.MANAGE_ROLES],
      render: () => <RolesAndPermissionsPanel />,
    },
    {
      // AC-Users-1: Tab label renamed to "Users" (was "Members & Invites").
      // Tab value stays 'members' — changing it would break existing URL bookmarks.
      // Backward-compat alias: ?tab=members-and-invites → handled via URL routing below.
      value: 'members',
      label: 'Users',
      anyPermissions: [PERMISSIONS.VIEW_USERS, PERMISSIONS.MANAGE_USERS],
      render: () => <MembersAndInvitesPanel />,
    },
    // AC-ALX-6: Knowledge & RAG tab removed — Embedding Model + Backfill moved into AI & LLM tab.
    // The ?tab=knowledge deep link is handled via TAB_ALIASES below (knowledge → ai).
    // NAV-1: Constitution tab removed from System Settings.
    // It now lives in Structure Settings where it belongs (governance structure).
  ];

  const visibleTabs = useMemo(
    () => allTabs.filter((tab) => tab.anyPermissions === null || hasAnyPermission(tab.anyPermissions)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasAnyPermission],
  );

  // ── URL-based tab routing ──────────────────────────────────────────────────
  // AC-Users-1: ?tab=members-and-invites (legacy bookmark) maps to 'members'.
  // Other tab values are accepted as-is for forward-compat and consistent UX.
  //
  // We use a controlled Tabs + one-time deep-link consume pattern (same as
  // StructureSettingsPage) so that the tab param is only read on mount and
  // manual tab changes by the user are not re-overridden.
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  /** Map legacy / external tab aliases to current tab values. */
  const TAB_ALIASES: Record<string, string> = {
    'members-and-invites': 'members',
    // AC-ALX-6: ?tab=knowledge deep links now land on AI & LLM tab
    'knowledge': 'ai',
  };

  // Default to first visible tab value (populated after permissions load).
  const [activeTab, setActiveTab] = useState<string>('');
  const deepLinkConsumedRef = useRef(false);

  // Resolve the requested tab param to a known tab value or alias.
  const resolveTabParam = (param: string | null): string | null => {
    if (!param) return null;
    // Check alias map first (backward-compat URLs)
    if (TAB_ALIASES[param]) return TAB_ALIASES[param];
    // Accept the param directly if it matches a known tab value
    if (visibleTabs.some((t) => t.value === param)) return param;
    return null;
  };

  React.useEffect(() => {
    if (deepLinkConsumedRef.current) return;
    if (visibleTabs.length === 0) return; // wait for permissions

    const resolved = resolveTabParam(tabParam);
    const resolvedAndVisible = resolved && visibleTabs.some((t) => t.value === resolved)
      ? resolved
      : null;

    // Default: first visible tab. Deep link: resolved visible tab.
    const target = resolvedAndVisible || visibleTabs[0].value;
    deepLinkConsumedRef.current = true;
    setActiveTab(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs, tabParam]);

  return (
    <RorosPage
      title="System Settings"
      icon={<SettingsIcon className="h-5 w-5" />}
    >
      {visibleTabs.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center p-8">
          <h2 className="text-lg font-semibold text-foreground mb-2">Access restricted</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            You do not have permission to view any system settings. Ask an
            administrator to grant access.
          </p>
        </div>
      ) : (
        <Tabs value={activeTab || visibleTabs[0].value} onValueChange={setActiveTab} className="mt-4">
          <TabsList>
            {visibleTabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {visibleTabs.map((tab) => (
            <TabsContent key={tab.value} value={tab.value}>
              {tab.render()}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </RorosPage>
  );
}
